<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  useTemplateRef,
  ref,
  watch,
  getCurrentInstance,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { downloadAttachment } from 'shared/helpers/downloadAttachment';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useExpandableContent } from 'shared/composables/useExpandableContent';
import { tryUseMessageContext } from '../provider.js';
import { MESSAGE_TYPES } from '../constants';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
  allowTranscribe: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const store = useStore();
const messageContext = tryUseMessageContext();
const isTranscribing = ref(false);

const transcribedText = computed(() =>
  (props.attachment.transcribedText || '').trim()
);
const transcriptionFailed = computed(
  () => props.attachment.transcriptionStatus === 'failed'
);
const isIncoming = computed(
  () => messageContext?.messageType?.value === MESSAGE_TYPES.INCOMING
);
const showTranscript = computed(
  () => Boolean(transcribedText.value) && props.showTranscribedText
);
const showTranscribeAction = computed(
  () =>
    props.allowTranscribe &&
    isIncoming.value &&
    !transcribedText.value &&
    Boolean(messageContext?.conversationId?.value)
);
const showTranscriptPanel = computed(
  () =>
    showTranscript.value || showTranscribeAction.value || isTranscribing.value
);

const { contentElement, showReadMore, showReadLess, toggleExpanded } =
  useExpandableContent({
    maxLines: 3,
    useResizeObserverForCheck: true,
  });

watch(transcribedText, text => {
  if (text) isTranscribing.value = false;
});

const transcribeAudio = async () => {
  if (isTranscribing.value) return;

  const conversationId = messageContext?.conversationId?.value;
  const messageId = messageContext?.id?.value || props.attachment.messageId;
  if (!conversationId || !messageId) return;

  isTranscribing.value = true;
  try {
    await store.dispatch('transcribeMessage', { conversationId, messageId });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('CONVERSATION.AUDIO_TRANSCRIPTION.ERROR');
    useAlert(errorMessage);
  } finally {
    isTranscribing.value = false;
  }
};

// Active Storage can 404 for a few seconds after message.created, while the
// upload is still finishing. A media element that fails once never retries the
// same URL, so each attempt needs a fresh cache buster.
const MAX_AUDIO_LOAD_ATTEMPTS = 6;

const withCacheBuster = url => {
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('t', Date.now());
    return parsed.toString();
  } catch {
    return url;
  }
};

const sourceUrl = ref(withCacheBuster(props.attachment?.dataUrl));
const isReady = ref(false);
const loadFailed = ref(false);
const retryAttempt = ref(0);
let retryTimer = null;
let durationTimer = null;
let resolvingDuration = false;
let suppressLoadError = false;

const audioType = computed(() => {
  const extension = props.attachment.extension?.toLowerCase();
  const url = props.attachment.dataUrl?.toLowerCase();

  if (extension === '.mp3' || extension === 'mp3') return 'audio/mpeg';
  if (extension === '.wav' || extension === 'wav') return 'audio/wav';
  if (extension === '.ogg' || extension === 'ogg' || extension === '.oga')
    return 'audio/ogg';
  if (extension === '.m4a' || extension === 'm4a') return 'audio/mp4';

  if (url?.includes('.mp3')) return 'audio/mpeg';
  if (url?.includes('.wav')) return 'audio/wav';
  if (url?.includes('.ogg') || url?.includes('.oga')) return 'audio/ogg';
  if (url?.includes('.m4a')) return 'audio/mp4';

  return 'audio/mpeg';
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

// WebM/Opus voice notes often omit a duration header, so the element reports
// Infinity until a seek forces the browser to measure the file.
const resolveStreamingDuration = () => {
  const element = audioPlayer.value;
  if (!element || resolvingDuration) return;

  resolvingDuration = true;
  suppressLoadError = true;

  const finish = () => {
    if (!resolvingDuration) return;

    resolvingDuration = false;
    element.removeEventListener('timeupdate', finish);
    const nextDuration = element.duration;
    try {
      element.currentTime = 0;
    } catch {
      // The file may not be seekable yet.
    }
    suppressLoadError = false;
    if (Number.isFinite(nextDuration) && nextDuration > 0) {
      duration.value = nextDuration;
    }
    isReady.value = true;
  };

  element.addEventListener('timeupdate', finish);
  durationTimer = window.setTimeout(finish, 1500);

  try {
    element.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    finish();
  }
};

const syncDuration = () => {
  const element = audioPlayer.value;
  if (!element || resolvingDuration) return;

  const nextDuration = element.duration;
  if (Number.isFinite(nextDuration) && nextDuration > 0) {
    duration.value = nextDuration;
    return;
  }

  if (nextDuration === Infinity) resolveStreamingDuration();
};

const clearRetryTimer = () => {
  if (!retryTimer) return;
  window.clearTimeout(retryTimer);
  retryTimer = null;
};

const clearDurationTimer = () => {
  if (!durationTimer) return;
  window.clearTimeout(durationTimer);
  durationTimer = null;
};

const onLoadedMetadata = () => {
  clearRetryTimer();
  loadFailed.value = false;
  retryAttempt.value = 0;
  const element = audioPlayer.value;
  if (element) element.playbackRate = playbackSpeed.value;

  if (element?.duration === Infinity) {
    resolveStreamingDuration();
    return;
  }

  isReady.value = true;
  syncDuration();
};

const reloadAudio = () => {
  sourceUrl.value = withCacheBuster(props.attachment?.dataUrl);
  nextTick(() => audioPlayer.value?.load());
};

const onAudioError = () => {
  if (suppressLoadError) return;
  // load() aborts the previous request and emits MEDIA_ERR_ABORTED.
  if (audioPlayer.value?.error?.code === 1) return;

  isReady.value = false;
  isPlaying.value = false;
  if (loadFailed.value || retryTimer || !props.attachment?.dataUrl) return;

  if (retryAttempt.value >= MAX_AUDIO_LOAD_ATTEMPTS) {
    loadFailed.value = true;
    return;
  }

  const delay = Math.min(1000 * (retryAttempt.value + 1), 3000);
  retryAttempt.value += 1;
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    reloadAudio();
  }, delay);
};

const retryLoad = () => {
  clearRetryTimer();
  retryAttempt.value = 0;
  loadFailed.value = false;
  isReady.value = false;
  duration.value = 0;
  currentTime.value = 0;
  reloadAudio();
};

watch(
  () => props.attachment?.dataUrl,
  url => {
    if (!url) return;
    retryLoad();
  }
);

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

onMounted(() => {
  const element = audioPlayer.value;
  if (!element) return;

  element.playbackRate = playbackSpeed.value;
  if (element.readyState < 1 || resolvingDuration) return;

  if (element.duration === Infinity) {
    resolveStreamingDuration();
    return;
  }

  isReady.value = true;
  syncDuration();
});

onUnmounted(() => {
  clearRetryTimer();
  clearDurationTimer();
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!Number.isFinite(time) || time < 0) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  if (resolvingDuration) return;
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const element = audioPlayer.value;
  if (!element || !isReady.value) return;

  const time = Number(event.target.value);
  element.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  const element = audioPlayer.value;
  if (!element || !isReady.value) return;

  if (!element.paused) {
    element.pause();
    isPlaying.value = false;
    return;
  }

  emitter.emit('pause_playing_audio', uid);
  element
    .play()
    .then(() => {
      isPlaying.value = !element.paused;
    })
    .catch(() => {
      isPlaying.value = false;
    });
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = props.attachment;
  await downloadAttachment({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    v-if="sourceUrl"
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    preload="metadata"
    @loadedmetadata="onLoadedMetadata"
    @durationchange="syncDuration"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
    @error="onAudioError"
  >
    <source :src="sourceUrl" :type="audioType" @error="onAudioError" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div
      v-if="loadFailed"
      class="flex gap-2 w-full flex-1 items-center justify-start px-1"
    >
      <Icon
        class="size-4 shrink-0 text-n-slate-11"
        icon="i-lucide-circle-off"
      />
      <span class="text-xs text-n-slate-11 flex-1">
        {{ $t('COMPONENTS.MEDIA.AUDIO_UNAVAILABLE') }}
      </span>
      <button
        type="button"
        class="border-0 h-6 px-2 text-xs font-medium text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3 rounded-2xl"
        @click="retryLoad"
      >
        {{ $t('COMPONENTS.MEDIA.RETRY') }}
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>
    <div v-else class="flex gap-1 w-full flex-1 items-center justify-start">
      <button
        class="p-0 border-0 size-8 disabled:opacity-60"
        :disabled="!isReady"
        @click="playOrPause"
      >
        <Spinner v-if="!isReady" :size="16" />
        <Icon
          v-else-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="showTranscriptPanel"
      class="flex flex-col gap-1.5 w-full px-2 pb-1.5 pt-2 border-t border-n-slate-12/10"
    >
      <div
        v-if="isTranscribing"
        class="flex items-center gap-1.5 text-xs text-n-slate-11"
      >
        <Spinner :size="14" />
        {{ $t('CONVERSATION.AUDIO_TRANSCRIPTION.TRANSCRIBING') }}
      </div>
      <button
        v-else-if="showTranscribeAction"
        type="button"
        class="flex items-center gap-1.5 self-start text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-transparent border-0 p-0 cursor-pointer"
        @click.stop="transcribeAudio"
      >
        <Icon class="size-3.5" icon="i-lucide-file-text" />
        {{
          transcriptionFailed
            ? $t('CONVERSATION.AUDIO_TRANSCRIPTION.RETRY')
            : $t('CONVERSATION.AUDIO_TRANSCRIPTION.ACTION')
        }}
      </button>
      <div
        v-else-if="showTranscript"
        class="text-sm text-n-slate-12 leading-relaxed break-words w-full"
      >
        <p ref="contentElement" :class="{ 'line-clamp-3': showReadMore }">
          {{ transcribedText }}
        </p>
        <button
          v-if="showReadMore"
          type="button"
          class="text-xs text-n-slate-11 underline cursor-pointer bg-transparent border-0 p-0 hover:text-n-slate-12 font-medium mt-1"
          @click.stop="toggleExpanded(true)"
        >
          {{ $t('CONVERSATION.AUDIO_TRANSCRIPTION.READ_MORE') }}
        </button>
        <button
          v-if="showReadLess"
          type="button"
          class="text-xs text-n-slate-11 underline cursor-pointer bg-transparent border-0 p-0 hover:text-n-slate-12 font-medium mt-1"
          @click.stop="toggleExpanded(false)"
        >
          {{ $t('CONVERSATION.AUDIO_TRANSCRIPTION.READ_LESS') }}
        </button>
      </div>
    </div>
  </div>
</template>
